/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View, Image } from "react-native";

import { NodeType } from "config/UtilityTypes";
import { ProfileImage, Text } from "UpduoLibrary";
import { GetKudosQuery } from "graphql/internal/generated";
import {
  skaleWhite,
  upduoBrandBrightBlue,
  upduoBrandPurple,
  upduoSecondaryRed,
  upduoKudosBlue,
  upduoKudosCyan,
  upduoKudosOrange,
  upduoKudosPurple,
} from "config/UpduoColors";

import friendly from "assets/kudos_icons/friendly.png";
import goodConversation from "assets/kudos_icons/good_conversation.png";
import helpfulTips from "assets/kudos_icons/helpful_tips.png";
import knowledgeable from "assets/kudos_icons/knowledgeable.png";
import { capitalize } from "config/Utilities";

const KUDOS_CATEGORIES = [
  "knowledgeable",
  "friendly",
  "goodConversation",
  "helpfulTips",
] as const;
type KudosCategory = typeof KUDOS_CATEGORIES[number];

const KUDOS_ICONS: Record<KudosCategory, any> = {
  friendly,
  goodConversation,
  helpfulTips,
  knowledgeable,
};

const KUDOS_COLORS: Record<KudosCategory, string> = {
  friendly: upduoKudosPurple,
  goodConversation: upduoKudosOrange,
  helpfulTips: upduoKudosCyan,
  knowledgeable: upduoKudosBlue,
};

function getCategories(
  kudo: NodeType<GetKudosQuery, "kudos2">
): KudosCategory[] {
  return KUDOS_CATEGORIES.filter(
    (category) => kudo[`category${capitalize(category)}`]
  );
}

export function KudoView({
  kudo,
}: {
  kudo: NodeType<GetKudosQuery, "kudos2">;
}) {
  return (
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        paddingTop: 16,
        paddingBottom: 12,
        paddingHorizontal: 20,
      }}
    >
      <ProfileImage size={40} userObject={kudo.sender} />
      <View
        style={{
          flexGrow: 1,
          flexShrink: 1,
          flexDirection: "column",
          marginLeft: 12,
        }}
      >
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          {getCategories(kudo).map((category) => (
            <KudosCategoryTag
              category={category}
              key={category}
              includeLabel={true}
            />
          ))}
        </View>
        {kudo.message ? (
          <View
            style={{
              marginTop: 8,
              borderRadius: 4,
              padding: 2,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                right: 0,
                opacity: 0.5,
              }}
            >
              <LinearGradient
                startColor={upduoBrandPurple}
                endColor={upduoBrandBrightBlue}
              />
            </View>
            <Text
              style={{
                padding: 8,
                borderRadius: 3,
                backgroundColor: skaleWhite,
                zIndex: 1,
              }}
            >
              {kudo.message}
            </Text>
          </View>
        ) : null}
        {kudo.approved === false ? (
          <Text
            style={{
              color: upduoSecondaryRed,
              marginTop: 8,
              fontSize: 12,
            }}
          >
            {
              "This message has been blocked due to potentially offensive language."
            }
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function LinearGradient({
  startColor,
  endColor,
}: {
  startColor: string;
  endColor: string;
}) {
  return (
    <div
      style={{
        backgroundImage: `linear-gradient(to right, ${startColor}, ${endColor})`,
        width: "100%",
        height: "100%",
      }}
    />
  );
}

const names: Record<KudosCategory, string> = {
  friendly: "Friendly",
  goodConversation: "Good conversation",
  helpfulTips: "Helpful Tips",
  knowledgeable: "Knowledgeable",
};

function KudosCategoryTag({
  category,
  includeLabel,
}: {
  category: KudosCategory;
  includeLabel: boolean;
}) {
  return (
    <a title={includeLabel ? undefined : names[category]}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: KUDOS_COLORS[category],
          borderRadius: 4,
          paddingHorizontal: 8,
          paddingVertical: 4,
          marginBottom: 4,
          marginRight: 4,
        }}
      >
        <Image
          source={KUDOS_ICONS[category]}
          width={24}
          height={24}
          style={{ width: 24, height: 24, resizeMode: "contain" }}
        />
        {includeLabel ? (
          <Text isHeading={true} style={{ fontSize: 14, marginLeft: 4 }}>
            {names[category]}
          </Text>
        ) : null}
      </View>
    </a>
  );
}

export function KudosCategoryIconGroup({
  kudo,
}: {
  kudo: NodeType<GetKudosQuery, "kudos2">;
}) {
  return (
    <View style={{ display: "flex", flexDirection: "row" }}>
      {getCategories(kudo).map((category) => (
        <KudosCategoryTag
          category={category}
          key={category}
          includeLabel={false}
        />
      ))}
    </View>
  );
}
